"use client"

import { Images } from "@/constants/images"
import { toImageSrc } from "@/lib/utils"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useEffect, useState } from "react"
import type { Swiper as SwiperInstance } from "swiper"
import "swiper/css"
import "swiper/css/free-mode"
import { Swiper, SwiperSlide } from "swiper/react"

import type { ServiceShowcaseProps } from "../types/config"
import { ServiceItem } from "./ServiceItem"
import "./serviceShowcase.css"

interface ServiceShowcaseContentProps extends Omit<ServiceShowcaseProps, "styles" | "responsiveType" | "animation"> {
  id: string
}

export const ServiceShowcaseContent: React.FC<ServiceShowcaseContentProps> = ({
  id,
  defaultBackgroundImage,
  defaultTitle,
  sectionTitle,
  logo,
  buttonText,
  buttonLink,
  defaultDescription,
  services,
  slidesPerView = 4,
  spaceBetween = 16,
  className,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(null)

  const currentData =
    activeIndex !== null && services[activeIndex]
      ? {
          backgroundImage: services[activeIndex].backgroundImage,
          title: services[activeIndex].title,
          description: services[activeIndex].description,
        }
      : {
          backgroundImage: defaultBackgroundImage,
          title: defaultTitle,
          description: defaultDescription,
        }

  const backgroundUrl = currentData.backgroundImage ? toImageSrc(currentData.backgroundImage) : null

  const logoUrl = logo ? toImageSrc(logo) : null

  const handleServiceClick = useCallback(
    (index: number) => {
      setActiveIndex(index)
      if (swiperInstance) {
        swiperInstance.slideToLoop(index, 200)
      }
    },
    [swiperInstance],
  )

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest(".service-item-container")) {
        setActiveIndex(null)
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  return (
    <div className={`relative flex items-center sm:h-[682px] overflow-hidden ${className}`}>
      <div className="absolute inset-0">
        {backgroundUrl && (
          <Image
            alt={defaultTitle}
            src={backgroundUrl}
            fill
            sizes="100vw"
            className="object-cover"
            key={backgroundUrl}
            priority
          />
        )}
      </div>

      <div className="absolute inset-0 bg-black opacity-70" />

      <div className="relative z-10 mx-auto items-center inset-0 flex justify-center">
        <div className="flex sm:flex-row flex-col sm:gap-[30px] gap-[22px] max-w-[1290px] px-[15px] items-center sm:p-0 pr-0 pb-[61px] pt-4">
          <div className="relative w-full sm:w-[32.5%]">
            {logoUrl && (
              <div className="sm:block hidden w-[133px] h-[48px]">
                <Image
                  src={logoUrl}
                  alt={defaultTitle}
                  width={133}
                  height={48}
                  className="object-cover w-full h-full"
                />
              </div>
            )}

            {/* Section title for small screens only */}
            {sectionTitle && (
              <h2 className="sm:hidden w-screen sm:w-full block text-base leading-6 font-black text-white whitespace-normal break-words mb-3">
                {sectionTitle}
              </h2>
            )}

            <h2 className="sm:text-[70px] sm:leading-[68px] text-[40px] leading-[40px] font-black text-white whitespace-normal break-words pt-4 pr-[15px] pb-[15px] max-w-full sm:w-full w-screen">
              {activeIndex !== null ? currentData.title : defaultTitle}
            </h2>

            <p className="sm:text-base sm:leading-[28px] text-sm leading-5 font-normal text-white whitespace-normal break-words pb-6">
              {activeIndex !== null ? currentData.description : defaultDescription}
            </p>

            <div className="flex items-center gap-[29px]">
              <div className="w-14 h-14">
                <Image src={Images.iconCart} alt="icon cart" className="object-cover w-full h-full" />
              </div>

              <Link
                href={buttonLink || "#"}
                className="text-base leading-[44px] font-bold text-white hover:text-white border border-white rounded pl-[25px] pr-[50px] btn-zoom-effect"
              >
                {buttonText || "Xem thêm"}
              </Link>
            </div>
          </div>

          <div className="w-full sm:w-[67.5%]">
            <div className="service-item-container w-full sm:!w-[1080px] flex flex-col sm:gap-[68px] gap-[25px]">
              <h2 className="hidden sm:block sm:text-xl sm:leading-[26px] text-lg leading-[25.2px] font-black text-white">
                {sectionTitle}
              </h2>
              <div className="w-full min-w-0 max-w-[100vw] overflow-hidden">
                <Swiper

                  className="service-showcase-swiper"
                  spaceBetween={20}
                  slidesPerView={"auto"}
                  loop={true}
                  grabCursor={true}
                  onSwiper={setSwiperInstance}
                  freeMode={true}
                >
                  {services.map((service, index) => (
                    <SwiperSlide key={index}>
                      <ServiceItem
                        service={service}
                        isActive={activeIndex === index}
                        onClick={() => handleServiceClick(index)}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
